/**
 * Rule 5 — Single source of truth for network status.
 * Never read navigator.onLine directly anywhere else in the app.
 */
import { useState, useEffect, useCallback } from 'react'

interface OnlineStatus {
  isOnline:      boolean
  wasOffline:    boolean      // true if user has been offline this session
  reconnectedAt: Date | null
}

export function useOnlineStatus(): OnlineStatus {
  const [isOnline,      setIsOnline]      = useState(navigator.onLine)
  const [wasOffline,    setWasOffline]    = useState(false)
  const [reconnectedAt, setReconnectedAt] = useState<Date | null>(null)

  const handleOnline = useCallback(() => {
    setIsOnline(true)
    setReconnectedAt(new Date())
  }, [])

  const handleOffline = useCallback(() => {
    setIsOnline(false)
    setWasOffline(true)
    setReconnectedAt(null)
  }, [])

  useEffect(() => {
    window.addEventListener('online',  handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online',  handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [handleOnline, handleOffline])

  return { isOnline, wasOffline, reconnectedAt }
}
